import { getParsedLocale } from "../../util/locale-utils";
import { encodedCookies } from "../../lib/cookies";

("use strict");

/**
 * Removes the query parameters from the path of the current router URL.
 * If query parameters are present, they are removed, otherwise, the original path is returned.
 *
 * @param {Object} router - The router object representing the current URL.
 * @returns {String} - The path without query parameters.
 */
const asPathWithoutParams = (router) => router?.asPath?.split("?")[0];

/**
 * Returns the PDP Redux locales based on the given catalogReduxState and asPath.
 * If the function is executed on the server-side (window object is undefined), it returns undefined.
 * If the required data is not found in the catalogReduxState, it returns undefined.
 *
 * @param {Object} catalogReduxState - The catalog Redux state object.
 * @param {string} asPath - The asPath to construct the key in catalogReduxState.
 * @return {string[]|undefined} The PDP Redux locales or undefined.
 */
const getPdpReduxLocales = (catalogReduxState, asPath) => {
  if (typeof window === "undefined") return;

  return catalogReduxState?.[`${window.location.origin}/api${asPath}`]?.data
    ?.productDetailPageData?.allLocalePids;
};

/**
 * Retrieves the Redux locales from the catalogReduxState based on the given catalog URL.
 *
 * @param {Object} catalogReduxState - The catalogReduxState object containing the Redux state for the catalog.
 * @param {string} asPath - The catalog URL to retrieve the locales for.
 *
 * @return {string[]} The array of locales retrieved from the catalogReduxState for the given catalog URL,
 * or undefined if the window is not available.
 */
const getCdpReduxLocales = (catalogReduxState, asPath) => {
  if (typeof window === "undefined") return;

  return catalogReduxState?.[`${window.location.origin}/api${asPath}`]
    ?.allLocaleNvalues;
};

/**
 * Retrieves the locale pids for the gift card redux props based on the current path.
 *
 * @param {Object} giftCardReduxProps - The gift card redux props object.
 * @param {string} asPath - The current path.
 *
 * @return {Array} - An array of locale pids if the path starts with "/p/gift-cards/giftcard-", otherwise undefined.
 */
const getGiftCardReduxLocales = (giftCardReduxProps, asPath) => {
  if (typeof window === "undefined") return;

  if (asPath.startsWith("/p/gift-cards/giftcard-")) {
    return giftCardReduxProps?.data?.allLocalePids;
  }
};

/**
 * Replace the pid or nValue value in the URL
 * @param {string} url - URL to replace
 * @param {string} newValue - new value to replace
 * @returns {string} - new URL with replaced pid or nValue value
 */
const replaceIdValue = (url, newValue) => {
  const giftCardRegex = /giftcard-(\d+).*\/prod(\d+)/;
  const prodRegex = /\/prod\d+/;
  const nRegex = /\/N-\w+/;

  if (giftCardRegex.test(url)) {
    return url.replace(giftCardRegex, `giftcard-${newValue}/_/prod${newValue}`);
  }

  if (prodRegex.test(url)) {
    return url.replace(prodRegex, "/" + newValue);
  }

  if (nRegex.test(url)) {
    return url.replace(nRegex, "/N-" + newValue);
  }

  return url;
};

/**
 * Get the URL of the region selector banner from the nav data.
 *
 * @param {object} nav - Nav data object
 * @returns {string} - URL of the region selector banner
 */
const getRegionBannerUrl = (nav) => {
  const banners = nav?.items[0]?.fields?.banners;
  const regionBannerData = banners?.find(
    (banner) => banner?.fields?.bannerType === "region-selector-banner"
  )?.fields;
  return regionBannerData?.url;
};

/**
 * Get the new i18n locale information based on cookies and parsed locale.
 *
 * @returns {string} - New i18n locale information
 */
const getNewI18LocaleInfo = () => {
  const cookies = encodedCookies();
  const countryCookie = cookies.get("Country");
  const formattedUsrLocale = getParsedLocale()?.countryCode;
  const localeMismatch =
    countryCookie &&
    ((countryCookie === "CA" && formattedUsrLocale !== "CA") ||
      (formattedUsrLocale === "CA" && countryCookie !== formattedUsrLocale));
  return localeMismatch && countryCookie === "CA" ? "en-ca" : "en-us";
};

/**
 * Get the locale value for the new i18n locale.
 *
 * @param {object} allLocaleValues - All locale values object
 * @param {string} newI18nLocale - New i18n locale information
 * @returns {string} - Locale value for the new i18n locale
 */
const getAllLocaleValue = (allLocaleValues, newI18nLocale) => {
  const newCountryLocale = newI18nLocale?.split("-")[1].toUpperCase();
  return allLocaleValues?.[newCountryLocale];
};

/**
 * Build the hyperlink based on the contentful URL, router, and new locale value.
 *
 * @param {string} contentfulUrl - URL from Contentful
 * @param {string} asPath - Router asPath string
 * @param {string} newLocaleValue - New locale value
 * @returns {string} - Constructed hyperlink
 */
const buildHyperLink = (contentfulUrl, asPath, newLocaleValue) => {
  if (!newLocaleValue) {
    return `${contentfulUrl}${asPath}`;
  }
  return `${contentfulUrl}${replaceIdValue(asPath, newLocaleValue)}`;
};

/**
 * Get the hyperlink for the region selector banner based on the nav data, all locale values, and router.
 *
 * @param {object} nav - Nav data object
 * @param {object} allLocaleValues - All locale values object
 * @param {object} router - Router object
 * @returns {string} - Constructed hyperlink for the region selector banner
 */
const getHyperLink = (nav, allLocaleValues, router) => {
  const url = getRegionBannerUrl(nav);
  const newI18nLocale = getNewI18LocaleInfo();
  const newLocaleValue = getAllLocaleValue(allLocaleValues, newI18nLocale);
  const contentfulUrl = url.replace(/\/$/, "");
  return buildHyperLink(contentfulUrl, router.asPath, newLocaleValue);
};

export {
  getPdpReduxLocales,
  getCdpReduxLocales,
  getGiftCardReduxLocales,
  getHyperLink,
  replaceIdValue,
  buildHyperLink,
  asPathWithoutParams,
};
